/**
 * Social profile links and the company website, shared by every form that
 * collects them and by the directory that publishes them.
 *
 * The rules live here, once, for the same reason the LinkedIn and phone rules
 * live in profileInputs.ts: signup, My Profile, the admin add-member drawer and
 * the admin edit drawer all collect the same values, and four copies of "what
 * counts as a valid Instagram URL" is four chances to disagree.
 *
 * A member's links are optional. Entering one is what publishes it to paid
 * members in the directory — there is no separate visibility switch — so every
 * form that collects them has to say so plainly.
 */

export type SocialPlatformId =
  | "x"
  | "instagram"
  | "facebook"
  | "youtube"
  | "tiktok"
  | "github"
  | "threads"
  | "bluesky"
  | "other";

export type SocialLink = {
  platform: SocialPlatformId;
  url: string;
};

type PlatformDefinition = {
  id: SocialPlatformId;
  label: string;
  /** Hosts accepted for this platform, without a `www.` prefix. */
  hosts: string[];
  /** Shown before the input, and the shape a bare handle is expanded into. */
  prefix?: string;
};

/**
 * The platforms offered, in the order they appear in the picker.
 *
 * A fixed list rather than free text: it lets a pasted handle be expanded into a
 * real URL, lets a typo'd host be refused, and lets the directory label each link
 * without guessing from the URL. "Other" is the escape hatch for a personal site
 * or a platform not listed, and it accepts any https URL.
 */
export const SOCIAL_PLATFORMS: PlatformDefinition[] = [
  { id: "x", label: "X (Twitter)", hosts: ["x.com", "twitter.com"], prefix: "https://x.com/" },
  { id: "instagram", label: "Instagram", hosts: ["instagram.com"], prefix: "https://instagram.com/" },
  { id: "facebook", label: "Facebook", hosts: ["facebook.com", "fb.com"], prefix: "https://facebook.com/" },
  { id: "youtube", label: "YouTube", hosts: ["youtube.com", "youtu.be"], prefix: "https://youtube.com/@" },
  { id: "tiktok", label: "TikTok", hosts: ["tiktok.com"], prefix: "https://tiktok.com/@" },
  { id: "github", label: "GitHub", hosts: ["github.com"], prefix: "https://github.com/" },
  { id: "threads", label: "Threads", hosts: ["threads.net", "threads.com"], prefix: "https://threads.net/@" },
  { id: "bluesky", label: "Bluesky", hosts: ["bsky.app"], prefix: "https://bsky.app/profile/" },
  { id: "other", label: "Other / personal site", hosts: [] },
];

/** How many a member may save. Generous, but not an unbounded write. */
export const MAX_SOCIAL_LINKS = 8;

/** The serialized column's maximum length, matched by the DB column. */
export const SOCIAL_LINKS_JSON_MAX = 2000;

export function socialPlatform(id: string): PlatformDefinition | undefined {
  return SOCIAL_PLATFORMS.find(platform => platform.id === id);
}

function hostOf(url: URL) {
  return url.hostname.toLowerCase().replace(/^www\./, "");
}

/**
 * A URL that is safe to publish as a link.
 *
 * https only, and no credentials in the URL. `javascript:` and `data:` are the
 * reason this is a scheme allow-list rather than a scheme block-list: a link a
 * member types is rendered as an anchor for other members to click, so anything
 * that is not plainly a web address must be refused rather than sanitized.
 */
function parseHttpsUrl(value: string): URL {
  const trimmed = value.trim();
  if (!trimmed) throw new Error("Enter a link or remove the row.");

  let url: URL;
  try {
    url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
  } catch {
    throw new Error("Enter a complete web address.");
  }

  if (url.protocol !== "https:") throw new Error("Links must start with https://.");
  if (url.username || url.password) throw new Error("Enter a plain web address with no sign-in details.");
  if (!url.hostname.includes(".")) throw new Error("Enter a complete web address.");
  return url;
}

/** Canonical form for any supported external HTTPS destination. */
export function normalizeHttpsUrl(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const url = parseHttpsUrl(trimmed);
  url.hostname = hostOf(url);
  return url.toString().replace(/\/$/, "");
}

/**
 * The value stored for one link.
 *
 * A bare handle is expanded using the platform's prefix, because people type
 * "janedoe" far more often than they paste a full profile URL. A full URL is
 * checked against that platform's hosts, so an Instagram URL filed under GitHub
 * is refused rather than published under the wrong label.
 */
export function normalizeSocialLink(platform: string, value: string): SocialLink | undefined {
  const definition = socialPlatform(platform);
  if (!definition) throw new Error("Choose a platform for this link.");

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const looksLikeUrl = trimmed.includes("://") || trimmed.includes(".") || trimmed.includes("/");
  if (!looksLikeUrl && definition.prefix) {
    const handle = trimmed.replace(/^@/, "");
    if (!/^[A-Za-z0-9._-]{1,100}$/.test(handle)) throw new Error(`Enter a valid ${definition.label} handle.`);
    return { platform: definition.id, url: `${definition.prefix}${handle}` };
  }

  const url = parseHttpsUrl(trimmed);
  if (definition.hosts.length && !definition.hosts.includes(hostOf(url))) {
    throw new Error(`That is not a ${definition.label} link.`);
  }

  /*
   * Rebuilt from the parsed URL rather than echoed back, so the stored value is
   * always normalized and never carries whatever whitespace was pasted with it.
   * `www.` is dropped for the same reason the host check ignores it: otherwise
   * the same profile pasted two ways is two different rows to the de-duplicator.
   */
  url.hostname = hostOf(url);
  return { platform: definition.id, url: url.toString().replace(/\/$/, "") };
}

/** The company website, held separately because it is the company's, not a profile. */
export function normalizeCompanyWebsite(value: string): string | undefined {
  return normalizeHttpsUrl(value);
}

/**
 * Validates and de-duplicates a whole set, for the save path.
 *
 * Rows left blank are dropped rather than rejected: an empty row is somebody who
 * added one and changed their mind, not an error worth blocking a save over.
 */
export function normalizeSocialLinks(links: readonly { platform: string; url: string }[]): SocialLink[] {
  const normalized: SocialLink[] = [];
  const seen = new Set<string>();

  for (const link of links) {
    const entry = normalizeSocialLink(link.platform, link.url);
    if (!entry) continue;
    const key = entry.url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(entry);
  }

  if (normalized.length > MAX_SOCIAL_LINKS) {
    throw new Error(`Save at most ${MAX_SOCIAL_LINKS} social links.`);
  }
  return normalized;
}

/**
 * Reads the stored column back.
 *
 * Fails soft to an empty list. This is display data on a profile page, and a row
 * whose JSON is malformed should cost that member their links, not the whole
 * directory request.
 */
export function parseSocialLinks(value: string | null | undefined): SocialLink[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry): entry is SocialLink =>
        Boolean(entry) &&
        typeof entry.platform === "string" &&
        typeof entry.url === "string" &&
        Boolean(socialPlatform(entry.platform)))
      .slice(0, MAX_SOCIAL_LINKS);
  } catch {
    return [];
  }
}

export function serializeSocialLinks(links: readonly SocialLink[]): string {
  return JSON.stringify(links);
}

/** "janedoe" from "https://x.com/janedoe" — what the input shows after the prefix. */
export function socialHandleFromUrl(link: SocialLink): string {
  const definition = socialPlatform(link.platform);
  if (definition?.prefix && link.url.startsWith(definition.prefix)) {
    return link.url.slice(definition.prefix.length);
  }
  return link.url;
}
